import React, { useState, useEffect } from 'react';
import { inferenceService, InferenceConfig } from '../services/inferenceService';
import '../styles/dashboard-pages.css';

const InferencePage: React.FC = () => {
  const [inferenceConfigs, setInferenceConfigs] = useState<InferenceConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<string | null>(null);
  const [inputData, setInputData] = useState<string>('');
  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    loadInferenceConfigs();
  }, []);

  const loadInferenceConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const configs = await inferenceService.listInference();
      console.log('[InferencePage] Loaded inference configs:', configs);
      setInferenceConfigs(configs);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to load inference configurations';
      setError(errorMsg);
      console.error('[InferencePage] Error loading inference configs:', err);
      alert(`Error loading inference configs: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const handleRunPrediction = () => {
    if (!selectedConfig || !inputData) {
      alert('Please select an inference configuration and provide input data');
      return;
    }
    
    // Mock prediction result - can be wired up later
    setResults({
      prediction: 'Sample prediction result',
      confidence: 0.95,
      processingTime: '0.23s',
      timestamp: new Date().toLocaleString(),
    });
  };

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Inference</h1>
        <p>Execute predictions using your inference configurations</p>
      </div>

      <div className="page-sections">
        <section className="section-card">
          <h2>Select Inference Configuration</h2>
          <p style={{ color: '#9ca3af', marginBottom: '20px' }}>
            Choose an inference configuration to run predictions
          </p>
          
          {loading ? (
            <div className="empty-state">
              <div className="empty-icon">⏳</div>
              <p>Loading inference configurations...</p>
            </div>
          ) : error ? (
            <div className="empty-state">
              <div className="empty-icon">⚠️</div>
              <p>Error: {error}</p>
              <button className="action-btn secondary" onClick={loadInferenceConfigs} style={{ marginTop: '12px' }}>
                Retry
              </button>
            </div>
          ) : inferenceConfigs.length > 0 ? (
            <div className="models-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
              {inferenceConfigs.map((config) => (
                <div
                  key={config.id}
                  className={`model-select-card ${selectedConfig === config.id ? 'selected' : ''}`}
                  onClick={() => setSelectedConfig(config.id)}
                  style={{
                    padding: '20px',
                    background: selectedConfig === config.id 
                      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(31, 41, 55, 0.5) 0%, rgba(30, 41, 59, 0.5) 100%)',
                    border: `1px solid ${selectedConfig === config.id ? 'rgba(99, 102, 241, 0.5)' : 'rgba(148, 163, 184, 0.1)'}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>🚀</div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#e4e4e7', marginBottom: '8px' }}>
                    {config.display_name || config.name}
                  </div>
                  {config.description && (
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>
                      {config.description}
                    </div>
                  )}
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>
                    Created: {formatDate(config.created_at)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                    Status: <span style={{ color: config.status === 'active' ? '#22c55e' : '#9ca3af' }}>
                      {config.status || 'active'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">🚀</div>
              <p>No inference configurations yet. Create your first inference configuration to get started.</p>
            </div>
          )}
        </section>

        {selectedConfig && (
          <>
            <section className="section-card">
              <h2>Input Data</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className="form-group">
                  <label>Input Data</label>
                  <textarea
                    rows={6}
                    placeholder="Enter your input data here (JSON, text, or image URL)..."
                    value={inputData}
                    onChange={(e) => setInputData(e.target.value)}
                    style={{
                      padding: '12px',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '8px',
                      background: 'rgba(31, 41, 55, 0.6)',
                      color: '#d1d5db',
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      resize: 'vertical',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button className="action-btn primary" onClick={handleRunPrediction}>
                    🚀 Run Prediction
                  </button>
                  <button className="action-btn secondary" onClick={() => setInputData('')}>
                    Clear
                  </button>
                </div>
              </div>
            </section>

            {results && (
              <section className="section-card">
                <h2>Prediction Results</h2>
                <div style={{
                  padding: '20px',
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '12px',
                }}>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '8px' }}>Prediction</div>
                    <div style={{ fontSize: '18px', fontWeight: 600, color: '#e4e4e7' }}>{results.prediction}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginTop: '16px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Confidence</div>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: '#22c55e' }}>{(results.confidence * 100).toFixed(1)}%</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Processing Time</div>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: '#e4e4e7' }}>{results.processingTime}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Timestamp</div>
                      <div style={{ fontSize: '14px', color: '#9ca3af' }}>{results.timestamp}</div>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InferencePage;

